import React, {
  SetStateAction,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import Breadcrumb, { BreadcrumbGroup } from '@amzn/meridian/breadcrumb';
import Row from '@amzn/meridian/row';
import { renderUrl, urls } from '../../config/Url';
import { FormattedMessage } from 'react-intl';
import { EmptyProfile, Profile } from '../../Types';
import { StateContext } from '../../GlobalStateStore';
import DataState from '../../Types/DataState';
import DataSource from '../../api';
import { getCurrentWeek, weeksList } from '../../Util';
import Box from '@amzn/meridian/box';
import Heading from '@amzn/meridian/heading';
import RenderTabsFromConfig, {
  ITabConfig,
} from '../../Util/Form/RenderTabsFromConfig';
import CCXDetailsTab, { CCXOverallTabTableDataItem } from './CCXDetailsTab';
import styles from './CCXDetailsPage.module.css';
import Toaster from '@amzn/meridian/toaster';
import Alert from '@amzn/meridian/alert';
import BackButton from '../../Util/generic/BackButton';
import { isFeatureEnabled, Feature } from '../../config/FeaturesConfig';
import PerformancePortalContext from '../PerformancePortalContext';
import Loader from '@amzn/meridian/loader';
import { useNavigate } from 'react-router';
import Filter from '../Team/FilterComponent/Filter';
import Page from '../../Types/Page';
import moment from 'moment';
import { TimeOption, VerticalOption } from '@amzn/aet-galactus-kit';
import {
  LighthouseFilters,
  TRAILING_DAYS,
} from '../Dashboard/DashboardComponents/LighthouseMetrics/types';
import { checkGoUser } from '../../Util/User';
import {
  convertToVerticalOption,
  decideVerticalOption,
} from '../../Util/Vertical';
import { EmptyFilters } from '../Dashboard/DashboardComponents/LighthouseMetrics/helper';
import {
  verticalCardStateSwitch,
  initVerticalCardState,
} from '../Dashboard/DashboardComponents/LighthouseMetrics/VerticalCardState';
import { cloneDeep } from 'lodash';

interface ICCXDetailsPageProps {
  userId: string;
  rating: string | undefined | null;
  weekValue?: string | undefined | null;
  channelValue?: string | undefined | null;
  segmentValue?: string | undefined | null;
  verticalValue?: string | undefined | null;
  skillValue?: string | undefined | null;
  marketplaceValue?: string | undefined | null;
}

interface ICCXFormState<TabIDs extends string = string> {
  tabConfig?: ITabConfig<TabIDs>;
}

type TabIDs =
  | 'CCX overall'
  | 'Empathy'
  | 'Courtesy'
  | 'Knowledgeable'
  | 'Understandability'
  | 'Helpfulness'
  | 'Efficiency';

const formatFromNewAPIResponse = (
  rootData: CCXOverallTabTableDataItem[] | undefined
): { [key: string]: CCXOverallTabTableDataItem[] } => {
  if (!rootData) {
    return {
      ccxCourtesy: [],
      ccxEfficiency: [],
      ccxEmpathy: [],
      ccxHelpfulness: [],
      ccxKnowledgeable: [],
      ccxOverall: [],
      ccxUnderstand: [],
    };
  }
  const rootStarObject: CCXOverallTabTableDataItem[] = [];
  const ccxCourtesy = cloneDeep(rootStarObject);
  const ccxEfficiency = cloneDeep(rootStarObject);
  const ccxEmpathy = cloneDeep(rootStarObject);
  const ccxHelpfulness = cloneDeep(rootStarObject);
  const ccxKnowledgeable = cloneDeep(rootStarObject);
  const ccxOverall = cloneDeep(rootStarObject);
  const ccxUnderstand = cloneDeep(rootStarObject);

  const addStarRatingProperty = (
    item: CCXOverallTabTableDataItem,
    rating: string
  ) => {
    return { ...item, starRating: +rating };
  };

  rootData.forEach((dataItem: CCXOverallTabTableDataItem) => {
    if (!!dataItem.ccxUnderstand) {
      ccxUnderstand.push(
        addStarRatingProperty(dataItem, dataItem.ccxUnderstand)
      );
    }
    if (!!dataItem.ccxOverall) {
      ccxOverall.push(addStarRatingProperty(dataItem, dataItem.ccxOverall));
    }
    if (!!dataItem.ccxKnowledgeable) {
      ccxKnowledgeable.push(
        addStarRatingProperty(dataItem, dataItem.ccxKnowledgeable)
      );
    }
    if (!!dataItem.ccxEmpathy) {
      ccxEmpathy.push(addStarRatingProperty(dataItem, dataItem.ccxEmpathy));
    }
    if (!!dataItem.ccxEfficiency) {
      ccxEfficiency.push(
        addStarRatingProperty(dataItem, dataItem.ccxEfficiency)
      );
    }
    if (!!dataItem.ccxHelpfulness) {
      ccxHelpfulness.push(
        addStarRatingProperty(dataItem, dataItem.ccxHelpfulness)
      );
    }
    if (!!dataItem.ccxCourtesy) {
      ccxCourtesy.push(addStarRatingProperty(dataItem, dataItem.ccxCourtesy));
    }
  });

  return {
    ccxCourtesy,
    ccxEfficiency,
    ccxEmpathy,
    ccxHelpfulness,
    ccxKnowledgeable,
    ccxOverall,
    ccxUnderstand,
  };
};

const CCXDetailsPage: React.FC<ICCXDetailsPageProps> = ({
  userId,
  rating,
  channelValue: cv,
  weekValue: wv,
  segmentValue: sv,
  verticalValue: vv,
  marketplaceValue: mv,
  skillValue: skv,
}) => {
  const {
    performancePortalContextValue = {},
    setPerformancePortalContextValue,
  } = useContext(PerformancePortalContext);
  const [selectedRating, setSelectedRating] = useState(rating);
  const [lastCheckedAll, setLastCheckedAll] = useState(false);
  const navigate = useNavigate();
  const [toasts, setToasts] = useState<{ id: string }[]>([]);
  const onCloseToast = (id: string) =>
    setToasts(toasts.filter(t => t.id !== id));

  const [defaultTab] = useState<TabIDs>('CCX overall');
  const [renderCount, setRenderCount] = useState(0);
  const globalState = useContext(StateContext);
  const [profile, setProfile] = useState<Profile>(EmptyProfile);
  const [profileState, setProfileState] = useState(DataState.LOADING);
  const { inspireOptions } = globalState?.state;
  const { cscEntityPrefix } = inspireOptions.data;
  const apiNaws = DataSource.getDataFeed('NAWS');
  const [showInitialToast, setShowInitialToast] = useState(true);
  const [formState, setFormState] = useState<ICCXFormState<TabIDs>>({});
  const [ccxDetailsData, setCCXDetailsData] = useState<any>(void 0);
  const [dataState, setDataState] = useState(DataState.LOADING);

  const [ccxDetailsEnabled] = useState(
    isFeatureEnabled(Feature.ENABLE_HISTORICAL_CCX_DETAIL)
  );

  const legacyExperience = !isFeatureEnabled(Feature.LIGHTHOUSE);
  const enableRAPRemovalAndCCXUpdate = isFeatureEnabled(
    Feature.ENABLE_RAP_REMOVAL_AND_CCX_UPDATE
  );
  const isGoUser = checkGoUser(profile.user.owner);
  const [filterVerticalValue, setFilterVerticalValue] = useState(vv ?? '');
  const [verticalOptions, setVerticalOptions] = useState<string[]>([]);
  const [verticalValue, setVerticalValue] = useState(vv ?? '');
  const [skillValue, setSkillValue] = useState(skv ?? 'ALL');
  const [channelValue, setChannelValue] = useState(cv ?? 'ALL');
  const [segmentValue, setSegmentValue] = useState(sv ?? 'ALL');
  const [marketplaceValue, setMarketplaceValue] = useState(mv ?? 'ALL');
  const [skillOptions, setSkillOptions] = useState<string[]>([]);
  const [channelOptions, setChannelOptions] = useState<string[]>([]);
  const [segmentOptions, setSegmentOptions] = useState<string[]>([]);
  const [marketplaceOptions, setMarketplaceOptions] = useState<string[]>([]);
  const weekOptions = [TRAILING_DAYS, ...weeksList()];
  const [weekValue, setWeekValue] = useState(wv ?? weekOptions[0]);
  const [usedPassedVertical, setUsedPassedVertical] = useState(false);

  const [currentTab, setCurrentTab] = useState(undefined);
  const [renderedFilterCount, setRenderedFilterCount] = useState(0);

  const changeFilterValue =
    (setter: (...args: any) => void) => (filterValue: any) => {
      setRenderedFilterCount(renderedFilterCount + 1);
      setter(filterValue);
    };

  const [lighthouseFilters, setLighthouseFilters] =
    useState<LighthouseFilters>(EmptyFilters);
  const [lighthouseFiltersState, setLighthouseFiltersState] = useState(
    DataState.LOADING
  );
  const [LHDefaultVertical, setLHDefaultVertical] = useState('');
  const [LHDefaultVerticalState, setLHDefaultVerticalState] = useState(
    DataState.LOADING
  );
  const [calledWithPassedVertical, setCalledWithPassedVertical] =
    useState(false);
  // Check for Vertical is in Vertical Options or not
  const [, setCheckOptions] = useState(false);
  const [checkState, setCheckState] = useState(DataState.LOADING);

  const isTM = profile.directReports.length > 0;

  /** Card State */
  const [, dispatch] = useReducer(
    verticalCardStateSwitch,
    initVerticalCardState
  );

  const setVerticalAndStateInit = (vertical: any) => {
    dispatch({
      type: 'init',
      vertical: convertToVerticalOption(vertical),
    });
  };

  const reRenderTabs = () => {
    const { details, lastAuditDate } = ccxDetailsData ?? {};
    if (!details) {
      return;
    }

    setRenderCount(renderCount + 1);

    const {
      ccxOverall,
      ccxEmpathy,
      ccxKnowledgeable,
      ccxCourtesy,
      ccxUnderstand,
      ccxHelpfulness,
      ccxEfficiency,
    } = formatFromNewAPIResponse(details);

    const redirectURLParams = `&verticalValue=${filterVerticalValue}&skillValue=${skillValue}&segmentValue=${segmentValue}&channelValue=${channelValue}&marketplaceValue=${marketplaceValue}&weekValue=${weekValue}`;

    const defaultProps = {
      cscEntityPrefix,
      loading: checkState === DataState.LOADING,
      isTM,
      selectedRating,
      setSelectedRating,
      lastCheckedAll,
      setLastCheckedAll,
      profile,
      userID: userId,
      lastUpdated: moment(lastAuditDate).utc().toDate(),
      renderCount,
      renderedFilterCount,
      redirectURLParams,
    };

    const newFormState: ICCXFormState<TabIDs> = {
      ...formState,
      tabConfig: {
        tabGroupAriaLabel: 'cs_perfportal_ccx_details_tab_group',
        content: {
          'CCX overall': {
            translationId: 'cs_perfportal_ccx_panel_ccx_overall',
            defaultMessage: 'CCX overall',
            markup: <CCXDetailsTab {...defaultProps} dataset={ccxOverall} />,
          },
          Empathy: {
            translationId: 'cs_perfportal_ccx_panel_empathy',
            defaultMessage: 'Empathy',
            markup: <CCXDetailsTab {...defaultProps} dataset={ccxEmpathy} />,
          },
          Courtesy: {
            translationId: 'cs_perfportal_ccx_panel_courtesy',
            defaultMessage: 'Courtesy',
            markup: <CCXDetailsTab {...defaultProps} dataset={ccxCourtesy} />,
          },
          Knowledgeable: {
            translationId: 'cs_perfportal_ccx_panel_knowledgeable',
            defaultMessage: 'Knowledgeable',
            markup: (
              <CCXDetailsTab {...defaultProps} dataset={ccxKnowledgeable} />
            ),
          },
          Understandability: {
            translationId: 'cs_perfportal_ccx_panel_understandability',
            defaultMessage: 'Understandability',
            markup: <CCXDetailsTab {...defaultProps} dataset={ccxUnderstand} />,
          },
          Helpfulness: {
            translationId: 'cs_perfportal_ccx_panel_helpfulness',
            defaultMessage: 'Helpfulness',
            markup: (
              <CCXDetailsTab {...defaultProps} dataset={ccxHelpfulness} />
            ),
          },
          Efficiency: {
            translationId: 'cs_perfportal_ccx_panel_efficiency',
            defaultMessage: 'Efficiency',
            markup: <CCXDetailsTab {...defaultProps} dataset={ccxEfficiency} />,
          },
        },
      },
    };
    setFormState(newFormState);
  };

  useEffect(() => {
    if (
      LHDefaultVerticalState === DataState.LOADED &&
      lighthouseFiltersState === DataState.LOADED
    ) {
      setCheckState(DataState.LOADED);
      return;
    }
    setCheckState(DataState.LOADING);
    setVerticalAndStateInit(verticalValue);
  }, [
    profile?.user?.userID,
    verticalValue,
    weekValue,
    skillValue,
    channelValue,
    lighthouseFilters,
    filterVerticalValue,
    verticalOptions,
    checkState,
    segmentValue,
    marketplaceValue,
    LHDefaultVerticalState,
    lighthouseFiltersState,
  ]);

  useEffect(() => {
    reRenderTabs();
  }, [checkState, selectedRating, lastCheckedAll, currentTab]);

  const setWeek = (e: React.FormEvent<HTMLSelectElement>): void => {
    setWeekValue(e.toString());
  };

  const weekValueChanger = (week: any) => {
    setSkillValue('ALL');
    setChannelValue('ALL');
    setSegmentValue('ALL');
    setMarketplaceValue('ALL');
    legacyExperience
      ? setFilterVerticalValue(VerticalOption.Total_CSC)
      : LHDefaultVertical === null
        ? setFilterVerticalValue(VerticalOption.Total)
        : setFilterVerticalValue(LHDefaultVertical);
    setWeek(week);
  };

  useEffect(() => {
    if (filterVerticalValue === '') return;
    setVerticalValue(
      decideVerticalOption(
        filterVerticalValue || LHDefaultVertical,
        isGoUser,
        enableRAPRemovalAndCCXUpdate
      )
    );
  }, [
    filterVerticalValue,
    isGoUser,
    LHDefaultVertical,
    enableRAPRemovalAndCCXUpdate,
  ]);

  useEffect(() => {
    dispatch({
      type: 'init',
      vertical: verticalValue,
    });
  }, [verticalValue]);

  useEffect(() => {
    if (
      LHDefaultVerticalState === DataState.LOADING ||
      lighthouseFiltersState === DataState.LOADING
    )
      return;

    if (legacyExperience) {
      setFilterVerticalValue(VerticalOption.Total_CSC);
      return;
    }

    /**
     * @ToDo - This logic is based on Aggregated is first element - It is first element for now
     * If we have other vertical which has higher rank of Total - then we need to think how we set verticalValue
     * when default vertical is null
     * */
    if (
      LHDefaultVertical === null ||
      !lighthouseFilters.verticals.includes(LHDefaultVertical)
    ) {
      setFilterVerticalValue(VerticalOption.Total);
    } else {
      setFilterVerticalValue(convertToVerticalOption(LHDefaultVertical));
    }
    setCheckState(DataState.LOADED);
  }, [lighthouseFiltersState, LHDefaultVerticalState]);

  // Initalize Vertical-Card-State Map
  useEffect(() => {
    if (verticalValue === '') return;
    dispatch({
      type: 'init',
      vertical: verticalValue,
    });
  }, [verticalValue]);

  //Check Default Vertical is in lighthouse Filters or not
  useEffect(() => {
    if (
      LHDefaultVerticalState === DataState.LOADING ||
      lighthouseFiltersState === DataState.LOADING
    )
      return;

    if (!LHDefaultVertical) {
      setCheckOptions(false);
      setCheckState(DataState.LOADED);
      return;
    }
    setCheckOptions(lighthouseFilters.verticals.includes(LHDefaultVertical));
    setCheckState(DataState.LOADED);
  }, [lighthouseFiltersState, LHDefaultVerticalState]);

  useEffect(() => {
    if (profile.user.userID === '' || profileState === DataState.LOADING) {
      return;
    }

    // Initialize filter Options to Avoid Race Condition
    if (lighthouseFilters !== EmptyFilters) {
      setLighthouseFilters(EmptyFilters);
      setLighthouseFiltersState(DataState.LOADING);
    }

    const isProfileTm = profile.directReports.length > 0;

    const queryVariablesFilters = apiNaws.prepareLighthouseFiltersQuery(
      profile.user.userID,
      cscEntityPrefix,
      isProfileTm,
      weekValue,
      weekValue === TRAILING_DAYS ? TimeOption.DAILY : TimeOption.WEEKLY,
      isProfileTm ? profile.directReports : [],
      isProfileTm ? profile.directReports.map(report => false) : []
      // syncMetrics
    );

    apiNaws.getLighthouseFilters(
      queryVariablesFilters,
      setLighthouseFilters,
      (args: any) => {
        setLighthouseFiltersState(args);
        setCheckState(DataState.LOADED);
      }
    );
  }, [weekValue, profile.user.userID, profileState]);

  useEffect(() => {
    if (profile.user.userID === '' || profileState === DataState.LOADING) {
      return;
    }

    const queryVariables = apiNaws.prepareLighthouseDefaultVerticalQuery(
      profile.user.userID,
      cscEntityPrefix
    );

    apiNaws.getDefaultVertical(
      queryVariables,
      setLHDefaultVertical,
      (args: any) => {
        setLHDefaultVerticalState(args);
        setCheckState(DataState.LOADED);
      }
    );
  }, [weekValue, profile.user.userID, profileState]);

  useEffect(() => {
    if (usedPassedVertical && vv) {
      setVerticalValue(vv);
      setFilterVerticalValue(vv);
    }
  }, [usedPassedVertical]);

  // Set Filter Options
  useEffect(() => {
    if (
      profile?.user.userID === '' ||
      lighthouseFiltersState !== DataState.LOADED ||
      LHDefaultVerticalState !== DataState.LOADED
    ) {
      return;
    }

    // @ToDo - If filter options are null, we don't want to send a query when we click the link
    setSkillOptions(lighthouseFilters.skills);
    setChannelOptions(lighthouseFilters.channels);
    setSegmentOptions(lighthouseFilters.segments);
    // ToDo - Remove question marks after the release
    setMarketplaceOptions(lighthouseFilters.marketplaces ?? []);

    /**
     * Display Total as Aggregated - Make new array in the Order, that Total at first index
     */
    const verticalParam = lighthouseFilters.verticals;

    const tempVertical = [VerticalOption.Total, ...verticalParam];
    const sortedVertical = [...new Set(tempVertical)]; // Total is not default vertical - Regard it as Aggregated

    setVerticalOptions(sortedVertical);
    setCheckState(DataState.LOADED);
    setUsedPassedVertical(true);
  }, [lighthouseFiltersState, LHDefaultVerticalState, profile?.user?.userID]);

  // Reset check value to false and state to Loaded - since Default Check is used for the first time
  useEffect(() => {
    if (verticalValue === '') return;
    setCheckOptions(false);
    setCheckState(DataState.LOADED);
  }, [verticalValue, skillValue, channelValue, segmentValue, marketplaceValue]);

  const handleGetCCXDetailsData = (): void => {
    if (profile?.user?.userID) {
      const verticalForSave =
        vv &&
        !usedPassedVertical &&
        (!verticalOptions || verticalOptions?.includes(vv))
          ? vv
          : filterVerticalValue;

      if (vv && !calledWithPassedVertical && verticalForSave !== vv) {
        return;
      }
      const prefixedUserID = `${cscEntityPrefix}:${profile?.user?.userID}`;

      const timeOption =
        weekValue === TRAILING_DAYS ? TimeOption.DAILY : TimeOption.WEEKLY;

      const timestamp =
        weekValue === TRAILING_DAYS
          ? moment(moment.now()).format('YYYY-MM-DD')
          : weekValue;

      const ccxAPIParams = {
        userID: prefixedUserID,
        isTM,
        directReports: profile?.directReports?.map(
          ({ userID }) => `${cscEntityPrefix}:${userID}`
        ),
        vertical: verticalForSave,
        channel: channelValue,
        timeOption,
        timestamp,
        skill: skillValue,
        segment: segmentValue,
        marketplace: marketplaceValue,
        syncMetrics: false,
      };

      // GGGG refers to the year of the week. This may be different from YYYY
      // When part of the week is from 2024 and part of it is from 2025. e.g. 2024-12-30 (Monday)
      // In such cases, GGGG refers to the new year (e..g 2025) and YYYY refers to the previous year (e.g. 2024).
      const contactStatusParams = {
        users: isTM ? ccxAPIParams.directReports : [prefixedUserID],
        startWeek:
          weekValue === TRAILING_DAYS
            ? moment().subtract(7, 'days').utc().format('GGGG-WW')
            : moment(weekValue, 'GGGG-WW')
                .subtract(7, 'days')
                .utc()
                .format('GGGG-WW'),
        endWeek:
          weekValue === TRAILING_DAYS
            ? moment().utc().format('GGGG-WW')
            : moment(weekValue, 'GGGG-WW').utc().format('GGGG-WW'),
      };
      // update data w/ api call
      apiNaws.getCCXDetailsData(
        ccxAPIParams,
        contactStatusParams,
        setCCXDetailsData,
        setDataState
      );
      setCalledWithPassedVertical(true);
    }
  };

  // Get profile data for user
  useEffect(() => {
    if (inspireOptions.state === DataState.LOADED && userId) {
      const queryMigrationVariables = apiNaws.prepareQuery(
        getCurrentWeek(),
        userId,
        cscEntityPrefix
      );
      apiNaws.getProfileData(
        queryMigrationVariables,
        setProfile,
        setProfileState
      );
    }
  }, [inspireOptions.state, userId]);

  useEffect(() => {
    reRenderTabs();
  }, [
    weekValue,
    filterVerticalValue,
    skillValue,
    segmentValue,
    marketplaceValue,
    channelValue,
    verticalValue,
    ccxDetailsData,
    performancePortalContextValue?.dashboardFilters,
  ]);

  useEffect(() => {
    handleGetCCXDetailsData();
  }, [weekValue, skillValue, segmentValue, marketplaceValue, channelValue]);

  useEffect(() => {
    if (usedPassedVertical) {
      handleGetCCXDetailsData();
      reRenderTabs();
    }
  }, [verticalValue, filterVerticalValue]);

  if (
    profileState === DataState.ERROR ||
    dataState === DataState.ERROR ||
    checkState === DataState.ERROR
  ) {
    return (
      <Alert type='error'>
        <FormattedMessage
          id='cs_perfportal_error_reason_1'
          defaultMessage='Your credentials may be incorrect or inadequate.'
        />
      </Alert>
    );
  }

  if (!performancePortalContextValue?.dashboardFilters) {
    navigate(renderUrl('/dashboard'));
    return <Loader />;
  }

  const passedUserID = performancePortalContextValue?.passedCCXUserID;
  const backURL = urls.dashboard + (passedUserID ? `/${passedUserID}` : '');

  const breadcrumbs = (
    <div className={styles.breadcrumbsContainer}>
      <BreadcrumbGroup>
        <Breadcrumb href={renderUrl(backURL)} onClick={navigate}>
          <FormattedMessage
            id='cs_perfportal_dashboard'
            defaultMessage='Dashboard'
            description='Dashboard button'
          />
        </Breadcrumb>
        <Breadcrumb>
          <FormattedMessage
            id='cs_perfportal_ccx_details'
            defaultMessage='CCX details'
          />
        </Breadcrumb>
      </BreadcrumbGroup>
      <BackButton backURL={backURL} />
    </div>
  );

  const filters = (
    <Box className={styles.filtersContainer}>
      <div className={styles.filterByText}>
        <FormattedMessage
          id='cs_perfportal_ccx_details_filter_by'
          defaultMessage='Filter by'
        />
      </div>
      <div className={styles.dropdownItemContainer}>
        <Filter
          verticalValue={filterVerticalValue}
          weekValue={weekValue}
          skillValue={skillValue}
          channelValue={channelValue}
          segmentValue={segmentValue}
          marketplaceValue={marketplaceValue}
          marketplaceOptions={marketplaceOptions}
          verticalOptions={verticalOptions}
          skillOptions={skillOptions}
          channelOptions={channelOptions}
          segmentOptions={segmentOptions}
          weekOptions={weekOptions}
          filterTruncate={{
            '512px': '100%',
            '860px': '50%',
            '1210px': '25%',
          }}
          customDate={undefined}
          dateRange={undefined}
          setVerticalValue={changeFilterValue(setFilterVerticalValue)}
          setSkillValue={changeFilterValue(setSkillValue)}
          setChannelValue={changeFilterValue(setChannelValue)}
          weekValueChanger={changeFilterValue(weekValueChanger)}
          setSegmentValue={changeFilterValue(setSegmentValue)}
          setMarketplaceValue={changeFilterValue(setMarketplaceValue)}
          setCustomDate={undefined}
          setDateRange={undefined}
          page={Page.DASHBOARD}
          ccxDetailsPage={Page.HISTORICAL_CCX_DETAILS}
        />
      </div>
    </Box>
  );

  const ccxDetailsTabs = (
    <RenderTabsFromConfig<TabIDs>
      defaultTab={defaultTab}
      tabConfig={formState?.tabConfig}
      onTabSwitch={setCurrentTab}
    />
  );

  const warningToast = (
    <Toaster toasts={toasts} onCloseToast={onCloseToast}>
      {toast => (
        <Alert toast={true} onClose={toast.onClose} size='medium'>
          <FormattedMessage
            id='cs_perfportal_ccx_details_num_responses_text'
            defaultMessage='The number of CCX responses may not match your dashboard due to different data refresh schedules.'
          />
        </Alert>
      )}
    </Toaster>
  );

  return (
    <>
      <div className={styles.ccxDetailsContainer}>
        <Row>{breadcrumbs}</Row>
        {ccxDetailsEnabled && (
          <>
            <Row className={styles.headingContainer}>
              <Heading level={1} type='h400'>
                <FormattedMessage
                  id='cs_perfportal_ccx_details_heading'
                  defaultMessage='CCX Details'
                />
              </Heading>
            </Row>
            <Row>{filters}</Row>
            <Row>{ccxDetailsTabs}</Row>
            {warningToast}
          </>
        )}
      </div>
    </>
  );
};

export default CCXDetailsPage;
